import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import {
  Drawer,
  Button,
  Tooltip,
  message,
  Tabs,
  Card,
  Modal,
  Form,
  Input,
  Rate,
  Radio,
  Table,
} from "antd";
import {
  DownOutlined,
  ArrowsAltOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  FileTextOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";

type CorrelationValue = {
  correlationStatus: string;
  clinicalMatch: string;
  comments: string;
};

type CaseData = {
  headerText: string;
  imagePath: string;
  hisDocuments: {
    consultation: string;
    lab: string;
  };
  history: {
    "Patient History": string;
    "Clinical Notes": string;
    Medical: string;
  };
  activityLog: string[];
  findings: [string[], string[]];
  patientInfo?: {
    name: string;
    age: number;
    gender: "Male" | "Female" | "Other";
  };
  clinicalSummary?: {
    patientDetails: {
      name: string;
      age: number;
      gender: string;
      mrn: string;
      admissionDate: string;
      dischargeDate: string;
    };
    clinicalHistory: string;
    clinicalSummary: string;
    dischargeSummary: string;
    labResults: string;
    progressNotes: string;
  };
};

const reportJsonMap: Record<string, [string, string]> = {
  "10": ["siva", "siva"],
  "11": ["kesav", "kesav"],
  "12": ["naveen", "naveen"],
  "13": ["jiya", "jiya"],
  "14": ["niyati", "niyati"],
};

function ReportViewer({ jsonFileName, reportType }: { jsonFileName: string; reportType: 'clinicalSummary' | 'reportB' }) {
  const [html, setHtml] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!jsonFileName) return;
    
    if (reportType === 'clinicalSummary') {
      // For clinical summary, use the new clinical template
      const url = `/clinical_template.html?report=${jsonFileName}&type=${reportType}`;
      setHtml(`<iframe src="${url}" style="width:100%;height:100%;border:none;"></iframe>`);
    } else {
      // For Report B, use the existing template
      const url = `/report_template.html?report=${jsonFileName}&type=${reportType}`;
      setHtml(`<iframe src="${url}" style="width:100%;height:100%;border:none;"></iframe>`);
    }
  }, [jsonFileName, reportType]);

  if (error) return <div style={{ color: "red" }}>{error}</div>;
  if (!html) return <div>Loading content...</div>;

  return (
    <div
      dangerouslySetInnerHTML={{ __html: html }}
      style={{ height: "100%", overflow: "auto", padding: "20px" }}
    />
  );
}

const consultationColumns = [
  { title: "DATE", dataIndex: "date", key: "date" },
  { title: "REFERRING PHY", dataIndex: "referringPhy", key: "referringPhy" },
  { title: "DEPARTMENT", dataIndex: "department", key: "department" },
  {
    title: "DOCUMENTS",
    dataIndex: "documentUrl",
    key: "documentUrl",
    render: (url: string) =>
      url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#6c63ff" }}
        >
          View
        </a>
      ) : (
        <span style={{ color: "#aaa" }}>No document</span>
      ),
  },
];

const labColumns = [
  { title: "DATE", dataIndex: "date", key: "date" },
  { title: "TEST NAME", dataIndex: "testName", key: "testName" },
  { title: "REFERRING PHY", dataIndex: "referringPhy", key: "referringPhy" },
  { title: "DEPARTMENT", dataIndex: "department", key: "department" },
  {
    title: "DOCUMENTS",
    dataIndex: "documentUrl",
    key: "documentUrl",
    render: (url: string) =>
      url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#6c63ff" }}
        >
          View
        </a>
      ) : (
        <span style={{ color: "#aaa" }}>No document</span>
      ),
  },
];

const actionColors = {
  ACTIVATED: "#4CAF50",
  CREATED: "#2196F3",
  ASSIGNED: "#FF9800",
  OPENED: "#9C27B0",
  DRAFTED: "rgb(255, 14, 122)",
  REPORTED: "#ff0000",
  COMPLETED: "#4CAF50",
} as const;

type ActionType = keyof typeof actionColors;

const CorrelationCard = styled.div`
  background: #f9f9f9;
  padding: 1rem;
  border-radius: 12px;
  margin-top: 0.5rem;
  width: 96%;
  border: 1px solid #e8e8e8;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 1.2rem;
  font-size: 0.8rem;
`;

const CorrelationButton = styled.button`
  background: #6c63ff;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #5a52d5;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const PatientInfoContainer = styled.div`
  padding: 24px 24px 16px 24px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
`;

const PatientName = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: #333;
`;

const PatientDetails = styled.p`
  font-size: 1rem;
  color: #666;
  margin: 4px 0 0;
`;

const DocumentImage = styled.img`
  height: 200px;
  width: auto;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  margin-top: 16px;
  cursor: pointer;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const parseDateFromLog = (logString: string): Date => {
  let match = logString.match(
    /^(\d{2}-\d{2}-\d{4}), (\d{1,2})[ :]+(\d{2}) (AM|PM)/
  );
  if (match) {
    const [, datePart, hoursStr, minutesStr, ampm] = match;
    const [day, month, year] = datePart.split("-").map(Number);
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (ampm === "PM" && hours < 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;

    return new Date(year, month - 1, day, hours, minutes);
  }

  return new Date(0);
};

const parseLogEntryForDisplay = (
  logString: string
): { timestamp: string; message: string } => {
  const match = logString.match(
    /^((?:\d{2}-\d{2}-\d{4}), (?:\d{1,2})[ :]+(?:\d{2}) (?:AM|PM)): (.*)$/
  );

  if (match) {
    return { timestamp: match[1], message: match[2].trim() };
  }

  return { timestamp: "", message: logString };
};

const ClinicalCorrelation = () => {
  const { id } = useParams<{ id: string }>();

  const [leftJsonFileName, rightJsonFileName] = reportJsonMap[id ?? ""] || ["", ""];
  const [caseData, setCaseData] = useState<CaseData | null>(null);

  const [isPatientHistoryDrawerVisible, setIsPatientHistoryDrawerVisible] =
    useState(false);
  const [isCorrelatedInLeftSide, setIsCorrelatedInLeftSide] = useState(false);
  const [isCorrelatedInRightSide, setIsCorrelatedInRightSide] = useState(false);
  const [correlationValueInLeftSide, setCorrelationValueInLeftSide] =
    useState<CorrelationValue>({ correlationStatus: "", clinicalMatch: "", comments: "" });
  const [correlationValueInRightSide, setCorrelationValueInRightSide] =
    useState<CorrelationValue>({ correlationStatus: "", clinicalMatch: "", comments: "" });
  const [showCorrelationModal, setShowCorrelationModal] = useState(false);
    
  const [form] = Form.useForm();

  useEffect(() => {
    if (id) {
      const savedCorrelationA = localStorage.getItem(`correlation_details_A_${id}.cases`);
      if (savedCorrelationA) {
        setCorrelationValueInLeftSide(JSON.parse(savedCorrelationA));
        setIsCorrelatedInLeftSide(true);
      }
      const savedCorrelationB = localStorage.getItem(`correlation_details_B_${id}.cases`);
      if (savedCorrelationB) {
        setCorrelationValueInRightSide(JSON.parse(savedCorrelationB));
        setIsCorrelatedInRightSide(true);
      } else {
        // Set different statuses for different cases based on id
        const caseId = parseInt(id);
        let status = "-"; // Default pending
        if (caseId % 3 === 0) {
          status = "Yes"; // Correlated
        } else if (caseId % 3 === 1) {
          status = "No"; // Mismatch
        } else {
          status = "-"; // Pending
        }
        
        if (status !== "-") {
          const mockCorrelationB = {
            correlationStatus: status,
            clinicalMatch: status === "Yes" ? "Match" : "Mismatch",
            comments: status === "Yes" ? "Report correlates well with clinical findings." : "Discrepancy noted between report and clinical presentation.",
          };
          setCorrelationValueInRightSide(mockCorrelationB);
          setIsCorrelatedInRightSide(true);
          // Also save to localStorage
          localStorage.setItem(`correlation_details_B_${id}.cases`, JSON.stringify(mockCorrelationB));
        }
      }
    }

    if (leftJsonFileName) {
      fetch(`/report_data/${leftJsonFileName}.json`)
        .then((res) => res.json())
        .then((data) => {
          if (data.caseData) {
            setCaseData(data.caseData);
          }
        })
        .catch((err) => {
          console.error('Error loading case data:', err);
        });
    }
  }, [id, leftJsonFileName]);
  
  const consultationDataUrl = caseData?.hisDocuments?.consultation || "";
  const labDataUrl = caseData?.hisDocuments?.lab || "";

  const hisDocumentsSectionData = {
    consultations: [
      {
        key: "1",
        date: "15 Mar 2021",
        referringPhy: "Dr. Kiran",
        department: "Neurology",
        documentUrl: consultationDataUrl,
      },
    ],
    labs: [
      {
        key: "1",
        date: "15 Mar 2021",
        testName: "Radiology Report",
        referringPhy: "Dr. Kiran",
        department: "Neurology",
        documentUrl: labDataUrl,
      },
    ],
  };

  const onDrawerClose = () => {
    setIsPatientHistoryDrawerVisible(false);
  };

  const handleImageFooterClick = () => {
    if (!caseData?.imagePath) {
      message.error("No image available for this case");
      return;
    }
    window.open(caseData.imagePath, "_blank");
  };

  const handlePatientHistoryFooterClick = () => {
    setIsPatientHistoryDrawerVisible(true);
  };

  if (!caseData) {
    return <div>Loading case data...</div>;
  }

  return (
    <MainContainer>
      <style>{`html, body { overflow: hidden !important; }`}</style>
      <PageHeaderContainer>
        <PageHeaderLeft>
          <PageHeader>{caseData.headerText}</PageHeader>
          <SubHeader>
            <a
              href="#"
              style={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                color: " #9370DB",
                gap: "0.3rem",
                fontSize: "0.7rem",
              }}
            >
              Open in Pacs Viewer
              <ViewInNewTabButtonIcon
                src={"/icons/open-in-new-tab-white.svg"}
              />
            </a>
          </SubHeader>
        </PageHeaderLeft>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button
              onClick={() => {
                console.log('Correlate button clicked');
                setShowCorrelationModal(true);
                
                // Load previously selected values
                if (correlationValueInRightSide.correlationStatus) {
                  form.setFieldsValue({
                    "Report B": correlationValueInRightSide.correlationStatus,
                    "comments": correlationValueInRightSide.comments
                  });
                }
              }}
              style={{ 
                background: 'white',
                color: '#9370db',
                border: '2px solid #9370db',
                borderRadius: '2rem',
                padding: '0.5rem 1rem',
                fontSize: '0.8rem',
                fontWeight: '500',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                position: 'relative', 
                zIndex: 9999,
                pointerEvents: 'auto'
              }}
            >
              {isCorrelatedInRightSide ? "Correlate" : "Correlate"}
            </button>
            
            {/* Correlation Modal */}
            {showCorrelationModal && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 10px)',
                right: 0,
                width: window.innerWidth > 1200 ? '600px' : 
                       window.innerWidth > 768 ? '500px' : 
                       window.innerWidth > 480 ? '400px' : '90vw',
                maxWidth: '90vw',
                background: '#fff',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                padding: '16px',
                zIndex: 1000,
                maxHeight: '90vh',
                overflowY: 'auto'
              }}>
                {/* Caret */}
                <div style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '20px',
                  width: 0,
                  height: 0,
                  borderLeft: '10px solid transparent',
                  borderRight: '10px solid transparent',
                  borderBottom: '10px solid white',
                }} />
                
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px',
                }}>
                  <h3 style={{ margin: 0, fontSize: '16px' }}>
                    Clinical Correlation
                  </h3>
                  <Button type="text" onClick={() => {
                    setShowCorrelationModal(false);
                    form.resetFields();
                  }}>
                    ×
                  </Button>
                </div>
                
                <Form form={form} layout="vertical">
                  
                  
                  <Form.Item
                    name="Report B"
                    label="Report is correlated with Clinical Summary"
                    rules={[{ required: true, message: 'Please select correlation status!' }]}
                  >
                    <Radio.Group>
                      <Radio value="Yes">Yes</Radio>
                      <Radio value="No">No</Radio>
                    </Radio.Group>
                  </Form.Item>
                  
                  <Form.Item
                    name="comments"
                    label="Comments"
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>
                  
                  <Form.Item style={{ marginBottom: 0, textAlign: 'center' }}>
                    <Button
                      type="primary"
                      onClick={() => {
                        form.validateFields().then((values) => {
                          const newCorrelationA = {
                            correlationStatus: "",
                            clinicalMatch: "",
                            comments: "",
                          };
                          const newCorrelationB = {
                            correlationStatus: values["Report B"],
                            clinicalMatch: values["Report B"] === "Yes" ? "Match" : "Mismatch",
                            comments: values["comments"] || "",
                          };
                          
                          setCorrelationValueInLeftSide(newCorrelationA);
                          setCorrelationValueInRightSide(newCorrelationB);
                          setIsCorrelatedInLeftSide(false);
                          setIsCorrelatedInRightSide(true);
                          
                          if (id) {
                            localStorage.setItem(`correlation_details_A_${id}.cases`, JSON.stringify(newCorrelationA));
                            localStorage.setItem(`correlation_details_B_${id}.cases`, JSON.stringify(newCorrelationB));
                            
                            // Dispatch custom event to notify parent page
                            window.dispatchEvent(new Event('localStorageChange'));
                          }
                          
                          form.resetFields();
                          setShowCorrelationModal(false);
                          message.success("Report correlation completed successfully!");
                          
                          // Update parent page
                          if (window.opener) {
                            window.opener.location.reload();
                          }
                        });
                      }}
                      style={{
                        background: "#9370DB",
                        borderRadius: "2rem",
                        padding: '0 2rem'
                      }}
                    >
                      Submit
                    </Button>
                  </Form.Item>
                </Form>
              </div>
            )}
          </div>
          
          <button
            onClick={() => {
              message.success("Clinical correlation submitted successfully!");
              // Close the window and update parent
              setTimeout(() => {
                if (window.opener) {
                  window.opener.location.reload();
                }
                window.close();
              }, 1000);
            }}
            style={{ 
              background: '#9370db',
              color: 'white',
              border: 'none',
              borderRadius: '2rem',
              padding: '0.5rem 2rem',
              fontSize: '0.8rem',
              fontWeight: '500',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
            }}
          >
            Submit
          </button>
        </div>
      </PageHeaderContainer>
      
      <SplitScreenContainer>
        <PanelContainer>
          <PanelHeader>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                flexDirection: "row",
              }}
            >
              <span style={{ whiteSpace: "nowrap", color: "#6c63ff", fontWeight: "600" }}>Clinical Document</span>
            </div>

            {isCorrelatedInLeftSide && correlationValueInLeftSide.correlationStatus && (
              <CorrelationCard>
                <div
                  style={{
                    color: "#333",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  {`Status: ${correlationValueInLeftSide.correlationStatus} (${correlationValueInLeftSide.clinicalMatch})`}
                </div>
                {correlationValueInLeftSide.comments && (
                  <div style={{ color: "#666", lineHeight: 1.4, flex: 1 }}>
                    <strong>Comments: </strong>
                    {correlationValueInLeftSide.comments}
                  </div>
                )}
              </CorrelationCard>
            )}
          </PanelHeader>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <ReportViewer jsonFileName={leftJsonFileName} reportType="clinicalSummary" />
          </div>
          {isPatientHistoryDrawerVisible && (
            <PatientHistoryOverlay onClick={onDrawerClose} />
          )}
          <PatientHistoryViewer $isOpen={isPatientHistoryDrawerVisible}>
            <PatientHistoryCloseBtn onClick={onDrawerClose}>
              &times;
            </PatientHistoryCloseBtn>
            
            {caseData.patientInfo && (
              <PatientInfoContainer>
                <PatientName>{caseData.patientInfo.name}</PatientName>
                <PatientDetails>
                  {caseData.patientInfo.age} | {caseData.patientInfo.gender}
                </PatientDetails>
              </PatientInfoContainer>
            )}
            <PatientHistoryContent>
              <Tabs
                defaultActiveKey="1"
                items={[
                  {
                    key: "1",
                    label: "History",
                    children: (
                      <HistorySection>
                        <HistoryItem>
                          <HistoryLabel>Patient History</HistoryLabel>
                          <HistoryContent>
                            {caseData.history["Patient History"]}
                          </HistoryContent>
                        </HistoryItem>
                        <HistoryItem>
                          <HistoryLabel>Clinical Notes</HistoryLabel>
                          <HistoryContent>
                            {caseData.history["Clinical Notes"]}
                          </HistoryContent>
                        </HistoryItem>
                        <HistoryItem>
                          <HistoryLabel>Medical</HistoryLabel>
                          <HistoryContent>
                            {caseData.history["Medical"]}
                          </HistoryContent>
                        </HistoryItem>
                        
                      </HistorySection>
                    ),
                  },
                  {
                    key: "2",
                    label: "Past Procedures",
                    children: (
                      <div style={{ padding: "1rem" }}>
                        <p>No past procedures available for this case.</p>
                      </div>
                    ),
                  },
                  {
                    key: "3",
                    label: "HIS Documents",
                    children: (
                      <DocumentList>
                        <h3 style={{ marginBottom: "1rem" }}>
                          Previous Consultations
                        </h3>
                        {hisDocumentsSectionData.consultations.map((item) => (
                          <DocumentItem key={item.key}>
                            <DocumentIcon>
                              <FileTextOutlined />
                            </DocumentIcon>
                            <DocumentInfo>
                              <DocumentTitle>
                                {item.department} Consultation
                              </DocumentTitle>
                              <DocumentMeta>
                                {item.date} - {item.referringPhy}
                              </DocumentMeta>
                            </DocumentInfo>
                            {item.documentUrl && (
                              <DocumentLink
                                href={item.documentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </DocumentLink>
                            )}
                          </DocumentItem>
                        ))}

                        <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>
                          Previous Lab Results
                        </h3>
                        {hisDocumentsSectionData.labs.map((item) => (
                          <DocumentItem key={item.key}>
                            <DocumentIcon>
                            <FileTextOutlined />
                            </DocumentIcon>
                            <DocumentInfo>
                              <DocumentTitle>{item.testName}</DocumentTitle>
                              <DocumentMeta>
                                {item.date} - {item.department}
                              </DocumentMeta>
                            </DocumentInfo>
                            {item.documentUrl && (
                              <DocumentLink
                                href={item.documentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </DocumentLink>
                            )}
                          </DocumentItem>
                        ))}
                      </DocumentList>
                    ),
                  },
                  {
                    key: "4",
                    label: "Activity Log",
                    children: (
                      <ActivityLogContainer>
                        {[...caseData.activityLog]
                          .sort(
                            (a, b) =>
                              parseDateFromLog(b).getTime() -
                              parseDateFromLog(a).getTime()
                          )
                          .map((entry, index) => {
                            const { timestamp, message: fullMessage } =
                              parseLogEntryForDisplay(entry);

                            let message = fullMessage;
                            const details: { [key: string]: string } = {};

                            const detailKeys = ["Comment", "Reason"];
                            detailKeys.forEach((key) => {
                              if (message.includes(`${key}:`)) {
                                const parts = message.split(`${key}:`);
                                message = parts[0].trim();
                                details[key] = parts[1].trim();
                              }
                            });

                            let action: string | undefined;
                            let color: string = "#333";
                            let beforeAction: string;
                            let afterAction: string = "";
                            const knownActions = Object.keys(actionColors);

                            const foundAction = knownActions.find((act) =>
                              message.includes(` ${act} `)
                            );

                            if (foundAction) {
                              action = foundAction;
                              color =
                                actionColors[
                                  action as keyof typeof actionColors
                                ];
                              const parts = message.split(action);
                              beforeAction = parts[0];
                              afterAction = parts[1];
                            } else {
                              beforeAction = message;
                            }

                            return (
                              <ActivityLogItem key={index}>
                                <ActivityLogMessage>
                                  <div>
                                    {beforeAction}
                                    {action && (
                                      <ActionText color={color}>
                                        {action}
                                      </ActionText>
                                    )}
                                    {afterAction}
                                  </div>
                                  {details["Reason"] && (
                                    <ActivityDetail>
                                      <strong>Reason:</strong>{" "}
                                      {details["Reason"]}
                                    </ActivityDetail>
                                  )}
                                  {details["Comment"] && (
                                    <ActivityDetail>
                                      <strong>Comment:</strong>{" "}
                                      {details["Comment"]}
                                    </ActivityDetail>
                                  )}
                                </ActivityLogMessage>
                                <ActivityLogTimestamp>
                                  {timestamp}
                                </ActivityLogTimestamp>
                              </ActivityLogItem>
                            );
                          })}
                      </ActivityLogContainer>
                    ),
                  },
                ]}
              />
            </PatientHistoryContent>
          </PatientHistoryViewer>
        </PanelContainer>
        <Divider />
        <PanelContainer>
          <PanelHeader
            style={{ flexDirection: "column", alignItems: "stretch" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                flexDirection: "row",
              }}
            >
              <span style={{ whiteSpace: "nowrap", color: "#6c63ff", fontWeight: "600" }}>Radiological Report</span>
            </div>

            {isCorrelatedInRightSide && (
              <CorrelationCard>
                <div
                  style={{
                    color: "#333",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    marginRight: "1.2rem",
                    flexShrink: 0,
                  }}
                >
                  {`Status: ${correlationValueInRightSide.correlationStatus} (${correlationValueInRightSide.clinicalMatch})`}
                </div>
                <div
                  style={{
                    color: "#666",
                    lineHeight: 1.4,
                    flex: 1,
                    margin: 0,
                    fontSize: "0.8rem",
                    minWidth: 0,
                    maxWidth: "100%",
                  }}
                >
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <span>
                      {correlationValueInRightSide.comments && (
                        <>
                          <span
                            style={{ fontWeight: 500, whiteSpace: "nowrap" }}
                          >
                            Comments -
                          </span>
                          {correlationValueInRightSide.comments}
                        </>
                      )}
                    </span>
                  </div>
                </div>
              </CorrelationCard>
            )}
          </PanelHeader>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <ReportViewer jsonFileName={rightJsonFileName} reportType="reportB" />
          </div>
        </PanelContainer>
      </SplitScreenContainer>
      <Footer>
        <Tooltip title="Click to view image">
          <CustomeButton onClick={handleImageFooterClick}>
            Image View <DownOutlined style={{ marginLeft: "8px" }} />
          </CustomeButton>
        </Tooltip>
        <Tooltip title="Click to view Patient History" style={{ gap: "1rem" }}>
          <CustomeButton onClick={handlePatientHistoryFooterClick}>
            Patient History <ArrowsAltOutlined style={{ marginLeft: "8px" }} />
          </CustomeButton>
        </Tooltip>
      </Footer>
    </MainContainer>
  );
};

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f5f5f5;
  overflow: hidden;
`;

const SplitScreenContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const PanelContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
  border-right: 1px solid #e8e8e8;
  min-width: 0;
  overflow: hidden;
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 768px) {
    border-right: none;
    border-bottom: 1px solid #e8e8e8;
    
    &:last-child {
      border-bottom: none;
    }
  }
`;

const PanelHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #e8e8e8;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const Divider = styled.div`
  width: 1px;
  background: #e8e8e8;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 100%;
    height: 1px;
  }
`;

const PageHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: white;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const PageHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
  flex: 1;
`;

const PageHeader = styled.h1`
  font-size: 1rem;
  font-weight: bold;
  margin: 0;
  color: #222;
  background: white;
  word-wrap: break-word;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const SubHeader = styled.div`
  font-size: 1rem;
  color: #007bff;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const ViewInNewTabButtonIcon = styled.img`
  height: 1rem;
  width: auto;
  color: #9370db;
`;

const Footer = styled.footer`
  background: rgb(47, 47, 46);
  color: rgb(20, 142, 71);
  text-align: left;
  padding: 1rem 2rem;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.9rem;
  }
`;

const CustomeButton = styled(Button)`
  background: rgb(71, 70, 73);
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  &:hover {
    background: rgb(90, 89, 92);
    color: white;
  }
`;

const PatientHistoryViewer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 45%;
  height: 80%;
  padding: 1rem;
  background: white;
  z-index: 1000;
  transform: translateY(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
  transition: transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
  border-top-right-radius: 30px;
  box-shadow: 0 -2px 16px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: 100%;
    height: 70%;
  }
  
  @media (max-width: 480px) {
    height: 80%;
    padding: 0.75rem;
  }
`;

const PatientHistoryCloseBtn = styled.button`
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #ddd;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  color: #666;
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 1001;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #fff;
    color: #333;
    border-color: #999;
    transform: scale(1.1);
  }
`;

const PatientHistoryContent = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 0;
  min-width: 0;

  .ant-tabs-nav {
    padding: 0 24px;
    margin-bottom: 0;
    
    @media (max-width: 480px) {
      padding: 0 16px;
    }
  }

  .ant-tabs-content-holder {
    padding: 24px;
    
    @media (max-width: 480px) {
      padding: 16px;
    }
  }
`;

const HistorySection = styled.div``;

const HistoryItem = styled.div`
  margin-bottom: 1.5rem;
`;

const HistoryLabel = styled.h4`
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 0.5rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const HistoryContent = styled.p`
  font-size: 0.9rem;
  color: #555;
  margin: 0;
  line-height: 1.7;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const DocumentList = styled.div``;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 8px;
  background: #fcfcfc;
  border: 1px solid #e8e8e8;
  transition: all 0.2s;
  min-width: 0;

  &:hover {
    background: #f5f5f5;
    border-color: #d9d9d9;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  }
  
  @media (max-width: 480px) {
    padding: 8px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const DocumentIcon = styled.div`
  margin-right: 12px;
  font-size: 1.5rem;
  color: #6c63ff;
`;

const DocumentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DocumentTitle = styled.div`
  font-weight: 600;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const DocumentMeta = styled.div`
  font-size: 0.85rem;
  color: #888;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const DocumentLink = styled.a`
  color: #6c63ff;
  font-weight: 600;
  font-size: 0.9rem;
`;

const ActivityLogContainer = styled.div``;

const ActivityLogItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem 0;
  font-size: 0.9rem;
  min-width: 0;

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const ActivityLogMessage = styled.div`
  flex: 1;
  padding-right: 1rem;
  line-height: 1.5;
  min-width: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  @media (max-width: 480px) {
    padding-right: 0;
  }
`;

const ActivityLogTimestamp = styled.div`
  color: #888;
  white-space: nowrap;
  font-size: 0.85rem;
  flex-shrink: 0;
`;

const ActionText = styled.span<{ color: string }>`
  color: ${(props) => props.color};
  font-weight: 600;
`;

const ActivityDetail = styled.div`
  margin-top: 0.5rem;
  color: #555;
  font-size: 0.85rem;
  padding-left: 1rem;
  border-left: 2px solid #eee;
  margin-left: 4px;
`;

const PatientHistoryOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

export default ClinicalCorrelation;
